/* eslint-disable camelcase */
// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import Immutable from 'immutable';
import { Extent } from '@arcgis/core/geometry';
import MapView from './map-view';
import Button from '../../shared/button';
import { AppContext } from '../../hooks/use-app';
import ArcGisMapView from '../../shared/map/arc-gis-map-view';
import SearchPlugin from '../../shared/map/plugins/search';
import { buildGraphic } from '../../sketch-editor/graphics-handler';
import { GraphicStyle } from '../../sketch-editor/graphic-styles';
import { advancedGeometryEnabled, shouldUseEsriMaps } from '../../sketch-editor';
import Colorizer from '../../sketch-editor/colorizer';
import { polygonFromExtent } from '../../sketch-editor/graphic-elements';
import LayerChangeCoordinator from '../../shared/map/plugins/utils/layer-change-coordinator';
import StandardLayers from '../../shared/map/plugins/standard-layers';
import GeoJsonLayers from '../../shared/map/plugins/geo-json-layers';
import PortalLayers from '../../shared/map/plugins/portal-layers';
import LayerPickerPlugin from '../../shared/map/plugins/layer-picker-plugin';
import RecordSketchEditor from '../../shared/map/plugins/record-sketch-editor';
import FeatureFocusTracker from '../../shared/map/plugins/feature-focus-tracker';
import Basemap from '../../shared/map/plugins/basemap';
import DefaultPlugins from '../../shared/map/plugins/default';
import LayerSettingsReferenceHandler from '../../shared/map/plugins/utils/layer-settings-reference-handler';
import DebuggingLayers from '../../shared/map/plugins/debugging-layers';
import PublicFeatureServiceLayers from '../../shared/map/plugins/public-feature-service-layers';

const DEFAULT_ZOOM = 17;

export default class FeatureEditorMapView extends React.Component {
  static propTypes = {
    editable: PropTypes.bool.isRequired,
    editing: PropTypes.bool.isRequired,
    addMode: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    showValidationErrors: PropTypes.bool,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    geometry: PropTypes.object,
    onGeometryChange: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
    fetchNearbyRecords: PropTypes.func.isRequired,
    geometryTyped: PropTypes.object,
    form: PropTypes.object,
    status: PropTypes.string,
    parentView: PropTypes.string.isRequired,
  };

  static contextType = AppContext;

  static defaultProps = {
    visible: true,
  };

  constructor(props, context) {
    super(props, context);
    const { latitude, longitude, geometry } = this.props;

    const graphic = this.graphic(geometry);
    const center = this.findCenter(latitude, longitude, graphic);

    const { addMode } = this.props;

    const instanceSettingsReferenceHandler = LayerSettingsReferenceHandler(context.layerSettings);

    this.state = {
      editingGeometry: false,
      requestUpdateLayers: false,
      editingLocation: addMode || false,
      center,
      graphic,
      layerSettingsReferenceHandler: instanceSettingsReferenceHandler,
      layerChangeCoordinator: LayerChangeCoordinator(instanceSettingsReferenceHandler),
    };
  }

  componentDidMount() {
    this.shouldRefreshArcGIS();
  }

  componentWillUnmount(): void {
    this.state.layerChangeCoordinator.destroy();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If the parent component left edit mode, we also need to leave edit mode
    if (!nextProps.editable) {
      this.setState({ editingLocation: false });
    }
  }

  isTypeMismatch = () => {
    const { geometryTyped, geometry } = this.props;
    if (!geometryTyped || !geometry) {
      return false;
    }

    return geometry.type && !(geometryTyped._geometryTypes.includes(geometry.type));
  };

  fetchEditorState = () => {
    const {
      editable, status, loading, showValidationErrors, geometry,
    } = this.props;
    const typeMismatched = this.isTypeMismatch();

    return {
      status, editable, loading, showValidationErrors, geometry, typeMismatched,
    };
  };

  handleLocationClick = (event) => {
    const { editingLocation } = this.state;
    if (editingLocation) {
      const { onGeometryChange } = this.props;
      this.setState({ editingLocation: false });

      onGeometryChange(this.reasonableGeometry(event.latLng));
    }
  };

  shouldRefreshArcGIS = () => {
    const shouldRefresh = (this.state.requestUpdateLayers);
    if (shouldRefresh) {
      this.setState((prevState) => ({
        ...prevState,
        requestUpdateLayers: false,
      }));
    }
    return shouldRefresh;
  };

  handleEditClick = () => {
    const { editingGeometry, editingLocation } = this.state;

    if (editingGeometry) {
      this.setState({ editingGeometry: !editingGeometry });
    } else {
      this.setState({ editingLocation: !editingLocation });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  reasonableGeometry(latlng) {
    return {
      latitude: +latlng.lat().toFixed(6),
      longitude: +latlng.lng().toFixed(6),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  findCenter(latitude, longitude, graphic) {
    if (graphic) {
      const geom = graphic.geometry?.extent?.center;
      if (geom) {
        return [geom.latitude, geom.longitude];
      }
    }

    if (latitude && longitude) {
      return [latitude, longitude];
    }

    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  graphic(geometry) {
    const { form, status } = this.props;
    const color = Colorizer(form).getColor(status);
    const { preferences } = this.context;

    if (geometry) {
      return buildGraphic(geometry, color, GraphicStyle.TARGET_GEOMETRY);
    }

    const extents = preferences?.lastEditorMapExtents;

    if (extents) {
      const extent = new Extent({ ...extents });
      return polygonFromExtent(extent);
    }

    return null;
  }

  buildMapView(parentView, graphic) {
    // eslint-disable-next-line camelcase
    const { globals: { config: { esri_api_key, google_maps } } } = this.context;
    const { layerChangeCoordinator, layerSettingsReferenceHandler } = this.state;
    const {
      form, geometry, onGeometryChange, fetchNearbyRecords, geometryTyped,
    } = this.props;
    const initialValues = { form, geometry };
    const plugins = [
      Basemap(layerSettingsReferenceHandler, google_maps.api_key),
      DefaultPlugins(),
      SearchPlugin(),
      LayerPickerPlugin('editor'),
      StandardLayers(layerChangeCoordinator),
      GeoJsonLayers(layerChangeCoordinator),
      PortalLayers(layerChangeCoordinator, esri_api_key),
      PublicFeatureServiceLayers(layerChangeCoordinator),
      FeatureFocusTracker(graphic, this.context.preferences),
    ];

    if (geometryTyped) {
      plugins.push(RecordSketchEditor(
        initialValues, this.fetchEditorState, fetchNearbyRecords, onGeometryChange, geometryTyped._geometryTypes,
      ));
    }

    if (window.localStorage.getItem('layer-coordination-debugging') === 'true') {
      plugins.push(DebuggingLayers());
    }

    return <ArcGisMapView parentView={parentView} plugins={plugins} sx={{ width: '100%', height: 300 }} />;
  }

  buildTypeMismatchWarning() {
    return (
      <div
        className={styles.tip}
        style={{
          position: 'absolute',
          top: '64px',
          width: '280px',
          padding: '16px',
          zIndex: 1000,
          display: 'block',
        }}
      >
        <div style={{ marginBottom: 7 }}>
          This geometry isn&apos;t compatible with your current form configuration.
          If you continue, it will be removed. You can then create a new geometry that works with this form.
        </div>
        <Button
          affirm
          small
          onClick={() => { this.props.onGeometryChange(null); }}
        >
          Continue Editing
        </Button>
      </div>
    );
  }

  renderEditButton() {
    const { editable, geometryTyped } = this.props;
    const { editingGeometry, editingLocation } = this.state;
    const { globals } = this.context;

    const isEditing = editingGeometry || editingLocation;

    if (editable && !shouldUseEsriMaps(globals, geometryTyped)) {
      const buttonText = isEditing ? 'Done' : 'Edit Location';

      return (
        <Button
          affirm
          key="edit-location"
          style={{
            position: 'absolute',
            top: 11,
            right: 11,
            transition: 'all .15s ease-in-out',
          }}
          onClick={this.handleEditClick}
        >
          {buttonText}
        </Button>
      );
    }

    return null;
  }

  render() {
    const { styles } = FeatureEditorMapView;
    const {
      addMode,
      editing,
      editable,
      geometryTyped,
      latitude,
      longitude,
      visible,
      parentView,
    } = this.props;
    const { center, editingLocation, graphic } = this.state;
    const { preferences, globals } = this.context;

    const linesAndPolygonsEnabled = advancedGeometryEnabled(geometryTyped);

    let markers = null;

    if (latitude != null && longitude != null) {
      markers = new Immutable.List(
        Immutable.fromJS([{ location: { lat: latitude, lng: longitude } }]),
      );
    }

    const tipStyle = { display: editingLocation ? 'block' : 'none' };

    // if the feature being displayed has a location, use it. Or else use the saved extents.
    const googleExtents = center ? null : preferences?.lastMapExtents;

    const esriEnabled = shouldUseEsriMaps(globals, geometryTyped);

    const legacyView = (
      <MapView
        cursor={editingLocation ? 'crosshair' : ''}
        className={styles.map}
        visible={visible}
        editing={editing}
        onMapClick={this.handleLocationClick}
        center={center}
        extents={googleExtents}
        zoom={DEFAULT_ZOOM}
        circleMarkers={markers}
        editingGeometry={editable || addMode}
        parentView={parentView}
      />
    );

    const arcGISView = this.buildMapView(parentView, graphic);

    return (
      <div className={styles.container}>
        {editing
          && this.isTypeMismatch()
          && this.buildTypeMismatchWarning()}
        <div id="map-box" className={styles.map}>
          {esriEnabled ? arcGISView : legacyView}
        </div>
        {!linesAndPolygonsEnabled && this.renderEditButton()}
        {!linesAndPolygonsEnabled && !esriEnabled && (
          <div
            className={styles.tip}
            style={tipStyle}
          >
            Click the map to set the location
          </div>
        )}
      </div>
    );
  }
}

const styles = cssInJS({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },

  map: {
    width: '100%',
    height: '100%',
  },

  editable: {
    display: 'block',
  },

  tip: {
    background: 'rgba(255,255,255,.95)',
    color: '#000',
    position: 'absolute',
    left: '50%',
    top: 16,
    marginLeft: '-110px',
    width: 220,
    fontSize: 13,
    textAlign: 'center',
    padding: 7,
    borderRadius: 2,
    boxShadow: '0 2px 3px rgba(0,0,0,.3)',
  },
});

FeatureEditorMapView.styles = styles;
